// Every user-facing string in the portal, in English and Norwegian.
//
// Scope: portal UI chrome only — labels, buttons, static prose, table headers,
// tooltips, snackbar/notification text, dialog titles, and CLIENT-side
// validation errors (ones this app raises itself, e.g. "A name is required").
//
// Deliberately NOT covered here, and still English-only:
//   - Server-side email content (functions/services/cateringService.js,
//     portalAdminController.js) — making those language-aware needs a stored
//     language preference per portalUser and is a separate backend change.
//   - Server-side VALIDATION error messages that ride back as `err.error.message`
//     (e.g. "Only 3 of X can be ordered", the møtemat min/max/cap messages).
//     Those come pre-built as English sentences from cateringService.js's
//     PortalError throws. Translating them properly means the server
//     returning a code instead of a sentence — also a separate change. The
//     CLIENT-side fallback shown when there is no server message IS
//     translated (see e.g. catering.error.loadFailed).
//   - Actual DATA values from the API — product names, group names, employee
//     names, company names. These are real business data, never "translated"
//     regardless of UI language (see PORTAL.md's own warning about this
//     exact trap: group SLUGS collide, and the same care applies here —
//     nothing that came out of Firestore gets run through this dictionary).
export type Lang = 'en' | 'no';

export const TRANSLATIONS: Record<Lang, any> = {
  en: {
    shell: {
      title: 'Customer Portal',
      company: 'Company',
      allCompanies: 'All companies',
      overview: 'Overview',
      employees: 'Employees',
      catering: 'Meeting food',
      purchases: 'Purchases',
      help: 'Help',
      signOut: 'Sign out'
    },

    login: {
      title: 'Frigg Customer Portal',
      subtitle: "Manage your company's canteen users",
      email: 'Email',
      password: 'Password',
      signIn: 'Sign in',
      signingIn: 'Signing in…',
      forgotPassword: 'Forgot your password?',
      resetHelp: 'Enter your email address and we will send you a link to choose a new password.',
      sendResetLink: 'Send reset link',
      sending: 'Sending…',
      backToSignIn: 'Back to sign in',
      footer: 'Need access or having trouble? Contact',
      errorEnterCredentials: 'Enter your email and password.',
      errorEnterEmail: 'Enter your email address first.',
      errorSignInFailed: 'Could not sign in.',
      errorResetFailed: 'Could not send the reset email.',
      infoResetSent: 'If that address has portal access, a reset link is on its way.',
      errorInvalidEmail: 'That does not look like a valid email address.',
      errorAccountDisabled: 'This account has been deactivated. Contact your Frigg representative.',
      errorWrongCredentials: 'Wrong email or password.',
      errorTooManyAttempts: 'Too many attempts. Wait a few minutes and try again.',
      errorGeneric: 'Could not sign in. Please try again.'
    },

    overview: {
      title: 'Overview',
      subtitle: 'Your canteen users and what they bought in {month}.',
      month: 'Month',
      employees: 'Employees',
      activeInactive: '{active} active · {inactive} deactivated',
      purchases: 'Purchases',
      paidByEmployees: 'kr {amount} paid by employees',
      yourSubsidy: 'Your subsidy',
      subsidyFoot: 'excl. VAT, this month so far',
      creditOnAccounts: 'Credit on accounts',
      creditFoot: 'unspent balance held by employees',
      reportErrorSuffix: 'Employee numbers above are up to date.',
      yourGroups: 'Your groups',
      groupsIntro: 'Groups decide what your employees pay. They are set up together with Frigg — contact us if something needs changing.',
      subsidyPct: '{pct}% subsidised',
      subsidyNote: 'Your company covers {pct}% of the price on subsidised items.',
      creditPill: 'kr {amount} credit',
      creditNote: "Members are topped up automatically on the group's schedule.",
      noSubsidyPill: 'No subsidy',
      noSubsidyNote: 'Members pay full price.',
      noGroups: 'No groups are linked to your company yet. Contact Frigg to get set up.',
      errorLoadFailed: 'Could not load the overview.'
    },

    employees: {
      title: 'Employees',
      subtitle: 'Everyone who can buy on your company account. Add new colleagues, and deactivate anyone who has left — the change takes effect immediately.',
      add: 'Add employee',
      searchPlaceholder: 'Search name, email or employee no…',
      status: 'Status',
      statusAll: 'All',
      statusActive: 'Active',
      statusInactive: 'Deactivated',
      group: 'Group',
      allGroups: 'All groups',
      activeOfTotal: '{active} active / {total}',
      export: 'Export',
      loading: 'Loading employees…',
      colName: 'Name',
      colEmail: 'Email',
      colEmployeeNo: 'Employee no',
      colGroup: 'Group',
      colCredit: 'Credit',
      colStatus: 'Status',
      active: 'Active',
      deactivated: 'Deactivated',
      tipEdit: 'Edit',
      tipReset: 'Email them a link to set a new password',
      tipDeactivate: 'Deactivate',
      tipReactivate: 'Reactivate',
      tipCreditHistory: 'Credit history',
      tipRemove: 'Remove from company',
      tipShowCredit: 'Show credit history',
      noMatches: 'No employee matches those filters.',
      emptyPrefix: 'No employees yet. Use',
      emptySuffix: 'to register your first colleague.',
      errorNoGroup: 'No group is set up for your company yet — contact Frigg.',
      successAddedInvited: 'Employee added — they have been emailed a link to set their password.',
      successAddedNoInvite: 'Employee added. They will need a password link before they can log in.',
      errorAddFailed: 'Could not add the employee.',
      thatPerson: 'That person',
      errorDuplicate: '{name} is already registered in {group}.',
      successSaved: 'Saved.',
      errorSaveFailed: 'Could not save.',
      thisEmployee: 'this employee',
      confirmDeactivateTitle: 'Deactivate {name}?',
      confirmDeactivateMessage: 'They will not be able to buy anything on your company account from now on. Their history is kept and you can reactivate them at any time.',
      confirmDeactivateLabel: 'Deactivate',
      confirmRemoveTitle: 'Remove {name}?',
      confirmRemoveMessage: 'This removes them from your company. Use Deactivate instead if they might come back — removal is meant for someone added by mistake.',
      confirmRemoveLabel: 'Remove',
      successReactivated: 'Employee reactivated.',
      successDeactivated: 'Employee deactivated.',
      errorStatusFailed: 'Could not change status.',
      successRemoved: 'Employee removed.',
      errorRemoveFailed: 'Could not remove.',
      successResetSent: 'Password link sent to {email}',
      errorResetFailed: 'Could not send the link.',
      errorLoadFailed: 'Could not load employees.'
    },

    employeeDialog: {
      addTitle: 'Add employee',
      editTitle: 'Edit employee',
      fullName: 'Full name',
      workEmail: 'Work email',
      workEmailHint: 'They sign in to the canteen with this address.',
      email: 'Email',
      employeeNumber: 'Employee number (optional)',
      group: 'Group',
      subsidised: '{pct}% subsidised',
      monthlyCredit: 'monthly credit',
      groupHint: 'Decides what this person pays.',
      sendInvite: 'Email them a link to set their password now',
      cancel: 'Cancel',
      save: 'Save',
      errorNameRequired: 'A name is required.',
      errorGroupRequired: 'Pick a group.',
      errorEmailRequired: 'A valid work email is required.'
    },

    ledgerDialog: {
      currentBalance: 'Current balance',
      colDate: 'Date',
      colWhat: 'What',
      colAmount: 'Amount',
      colBalance: 'Balance',
      empty: 'No credit history yet.',
      close: 'Close',
      errorLoadFailed: 'Could not load the credit history.'
    },

    catering: {
      title: 'Meeting food',
      subtitle: 'Order food from the week\'s menu for a meeting or a course. Orders are confirmed by Frigg before anything is prepared — you will get an email either way.',
      noCanteen: 'There is no canteen linked to your company yet. Contact Frigg to set this up.',
      readOnly: 'Your account can see orders but not place them. Ask an owner or manager at your company.',
      newOrder: 'New order',
      location: 'Location',
      deliveryDate: 'Delivery date',
      datesNote: 'Only dates we can still plan for are listed.',
      loadingMenu: 'Loading the menu…',
      menuNotPublished: 'The menu for that week has not been published yet. Try a date in a week we have planned.',
      menuNotFinal: "This week's menu is not final — a product may still change. We will tell you if it affects your order.",
      colProduct: 'Product',
      colPrice: 'Price (ex. VAT)',
      colMin: 'Min',
      colQuantity: 'Quantity',
      colSum: 'Sum',
      tagVegetarian: 'Vegetarian',
      tagGlutenFree: 'Gluten free',
      tagLactoseFree: 'Lactose free',
      minWarning: 'min {min}',
      nothingAvailable: "Nothing on that day's menu is available for meeting food. Try another date.",
      whatFor: 'Reference / PO',
      whatForPlaceholder: 'e.g. Board meeting or PO number',
      comment: 'Comment (optional)',
      commentPlaceholder: 'Allergies, delivery time, room…',
      belowMinOne: 'One product is below its minimum order quantity — raise the quantity or remove it.',
      belowMinMany: '{count} products are below their minimum order quantity — raise the quantity or remove them.',
      belowOrderMin: 'This order totals {total} kr — orders must be at least {min} kr. Add more to send it.',
      products: '{count} product',
      productsPlural: '{count} products',
      inclVat: 'incl. VAT',
      sendOrder: 'Send order',
      sendingOrder: 'Sending…',
      yourOrders: 'Your orders',
      nothingOrdered: 'Nothing ordered yet.',
      colOrderDate: 'Date',
      colOrderNo: '#',
      colFor: 'For',
      colProducts: 'Products',
      colTotal: 'Total',
      colOrderStatus: 'Status',
      tipCancel: 'Cancel this order',
      statusConfirmed: 'Confirmed',
      statusPending: 'Pending approval',
      statusRejected: 'Rejected',
      statusCancelled: 'Cancelled',
      statusUnknown: 'Unknown',
      successSubmitted: 'Request #{number} sent. You will get an email when Frigg confirms it.',
      errorSubmitFailed: 'Could not send the order.',
      confirmCancelTitle: 'Cancel order #{number}?',
      confirmCancelApproved: 'This order has already been confirmed by Frigg. Cancelling it now will notify the kitchen.',
      confirmCancelPending: 'This request has not been confirmed yet, so nothing has been prepared.',
      confirmCancelLabel: 'Cancel order',
      successCancelled: 'Order cancelled.',
      errorCancelFailed: 'Could not cancel the order.',
      tooCloseToCancel: 'Too close to the delivery date to cancel — contact Frigg.',
      errorLoadFailed: 'Could not load meeting food ordering.',
      errorMenuLoadFailed: 'Could not load the menu for that date.'
    },

    purchases: {
      title: 'Purchases',
      subtitle: 'What your employees bought, and how much of it your company subsidised. This is an operational view — your invoice is the authoritative figure.',
      month: 'Month',
      group: 'Group',
      allGroups: 'All groups',
      searchPlaceholder: 'Search employee or product…',
      perEmployee: 'Per employee',
      allPurchases: 'All purchases',
      export: 'Export',
      loading: 'Loading purchases…',
      statPurchases: 'Purchases',
      statPaidByEmployees: 'Paid by employees',
      statSubsidyFoot: 'after your subsidy',
      statYourSubsidy: 'Your subsidy',
      statExVat: 'excl. VAT',
      colEmployee: 'Employee',
      colEmail: 'Email',
      colPurchases: 'Purchases',
      colEmployeePaid: 'Employee paid',
      colYourSubsidy: 'Your subsidy',
      colDate: 'Date',
      colProduct: 'Product',
      colPayment: 'Payment',
      empty: 'No purchases in this period.',
      errorLoadFailed: 'Could not load purchases.',
      csvSubsidyExVat: 'Company subsidy (ex VAT)'
    },

    help: {
      title: 'Help',
      subtitle: "What each page does, what your role can do, and how to reach Frigg for anything else.",
      replayWelcome: 'Show welcome tour again',
      pages: 'Pages',
      yourRole: 'Your role',
      colRole: 'Role',
      colManageEmployees: 'Manage employees',
      colOrderCatering: 'Order meeting food',
      roleOwner: 'Owner',
      roleManager: 'Manager',
      roleViewer: 'Viewer',
      yes: 'Yes',
      no: 'No',
      readOnly: 'Read only',
      signedInAsPrefix: 'You are signed in as',
      whatFriggHandles: 'What Frigg handles',
      whatFriggHandlesIntro: "Some things are commercial terms between your company and Frigg rather than your own data, so they aren't editable here — contact your Frigg representative for:",
      handledGroups: 'Setting up or changing a group (who belongs where, subsidy percentage, monthly credit)',
      handledCredit: "Adjusting an employee's credit balance",
      handledInvoicing: 'Invoicing and billing questions',
      handledPortalUsers: 'Adding another portal user for your company'
    },

    welcome: {
      title: 'Welcome',
      titleWithName: 'Welcome, {name}',
      intro: "This is where you manage your own canteen employees instead of emailing Frigg. Here's what each page does:",
      readOnlyNotice: "Your account is read-only — you can see everything here but can't make changes. Ask an owner or manager at your company if something needs updating.",
      footnote: "Need something that isn't here — new groups, subsidy changes, or an invoice question? Contact your Frigg representative.",
      readFullGuide: 'Read the full guide',
      gotIt: 'Got it, thanks'
    },

    guide: {
      overviewTitle: 'Overview',
      overviewBody: 'A monthly snapshot of your account: how many employees you have, what they bought, what your subsidy cost, and any credit still sitting on their balances. Change the month at the top to look back.',
      employeesTitle: 'Employees',
      employeesBody: 'Add a new hire or deactivate someone who has left. Deactivating stops their access immediately but keeps their history, so you can reactivate them later if they come back. Click a name to see their credit ledger.',
      cateringTitle: 'Meeting food',
      cateringBody: "Order from the week's planned menu for an upcoming meeting or course. Every order is a request — Frigg confirms it before anything is prepared, and you'll get an email either way.",
      purchasesTitle: 'Purchases',
      purchasesBody: "A record of everything bought on your company's account, so you can check spending without asking Frigg for a report."
    },

    confirm: {
      cancel: 'Cancel',
      confirm: 'Confirm'
    }
  },

  no: {
    shell: {
      title: 'Kundeportal',
      company: 'Bedrift',
      allCompanies: 'Alle bedrifter',
      overview: 'Oversikt',
      employees: 'Ansatte',
      catering: 'Møtemat',
      purchases: 'Kjøp',
      help: 'Hjelp',
      signOut: 'Logg ut'
    },

    login: {
      title: 'Frigg Kundeportal',
      subtitle: 'Administrer bedriftens kantinebrukere',
      email: 'E-post',
      password: 'Passord',
      signIn: 'Logg inn',
      signingIn: 'Logger inn…',
      forgotPassword: 'Glemt passord?',
      resetHelp: 'Skriv inn e-postadressen din, så sender vi deg en lenke for å velge nytt passord.',
      sendResetLink: 'Send tilbakestillingslenke',
      sending: 'Sender…',
      backToSignIn: 'Tilbake til innlogging',
      footer: 'Trenger du tilgang eller har problemer? Kontakt',
      errorEnterCredentials: 'Skriv inn e-post og passord.',
      errorEnterEmail: 'Skriv inn e-postadressen din først.',
      errorSignInFailed: 'Kunne ikke logge inn.',
      errorResetFailed: 'Kunne ikke sende e-posten for tilbakestilling.',
      infoResetSent: 'Hvis denne adressen har portaltilgang, er en tilbakestillingslenke på vei.',
      errorInvalidEmail: 'Det ser ikke ut som en gyldig e-postadresse.',
      errorAccountDisabled: 'Denne kontoen er deaktivert. Kontakt din Frigg-kontakt.',
      errorWrongCredentials: 'Feil e-post eller passord.',
      errorTooManyAttempts: 'For mange forsøk. Vent noen minutter og prøv igjen.',
      errorGeneric: 'Kunne ikke logge inn. Prøv igjen.'
    },

    overview: {
      title: 'Oversikt',
      subtitle: 'Kantinebrukerne deres og hva de har kjøpt i {month}.',
      month: 'Måned',
      employees: 'Ansatte',
      activeInactive: '{active} aktive · {inactive} deaktivert',
      purchases: 'Kjøp',
      paidByEmployees: 'kr {amount} betalt av ansatte',
      yourSubsidy: 'Deres subsidiering',
      subsidyFoot: 'eks. mva, denne måneden så langt',
      creditOnAccounts: 'Saldo på kontoer',
      creditFoot: 'ubrukt saldo hos ansatte',
      reportErrorSuffix: 'Antall ansatte over er likevel oppdatert.',
      yourGroups: 'Deres grupper',
      groupsIntro: 'Grupper bestemmer hva de ansatte betaler. De settes opp sammen med Frigg — kontakt oss hvis noe må endres.',
      subsidyPct: '{pct}% subsidiert',
      subsidyNote: 'Bedriften dekker {pct}% av prisen på subsidierte varer.',
      creditPill: 'kr {amount} i kreditt',
      creditNote: 'Medlemmer fylles automatisk på etter gruppens plan.',
      noSubsidyPill: 'Ingen subsidiering',
      noSubsidyNote: 'Medlemmer betaler full pris.',
      noGroups: 'Ingen grupper er koblet til bedriften ennå. Kontakt Frigg for å komme i gang.',
      errorLoadFailed: 'Kunne ikke laste oversikten.'
    },

    employees: {
      title: 'Ansatte',
      subtitle: 'Alle som kan handle på bedriftens konto. Legg til nye kolleger, og deaktiver de som har sluttet — endringen gjelder umiddelbart.',
      add: 'Legg til ansatt',
      searchPlaceholder: 'Søk navn, e-post eller ansattnummer…',
      status: 'Status',
      statusAll: 'Alle',
      statusActive: 'Aktive',
      statusInactive: 'Deaktivert',
      group: 'Gruppe',
      allGroups: 'Alle grupper',
      activeOfTotal: '{active} aktive / {total}',
      export: 'Eksporter',
      loading: 'Laster ansatte…',
      colName: 'Navn',
      colEmail: 'E-post',
      colEmployeeNo: 'Ansattnummer',
      colGroup: 'Gruppe',
      colCredit: 'Saldo',
      colStatus: 'Status',
      active: 'Aktiv',
      deactivated: 'Deaktivert',
      tipEdit: 'Rediger',
      tipReset: 'Send dem en lenke for å sette nytt passord',
      tipDeactivate: 'Deaktiver',
      tipReactivate: 'Reaktiver',
      tipCreditHistory: 'Saldohistorikk',
      tipRemove: 'Fjern fra bedriften',
      tipShowCredit: 'Vis saldohistorikk',
      noMatches: 'Ingen ansatte matcher disse filtrene.',
      emptyPrefix: 'Ingen ansatte ennå. Bruk',
      emptySuffix: 'for å registrere din første kollega.',
      errorNoGroup: 'Ingen gruppe er satt opp for bedriften ennå — kontakt Frigg.',
      successAddedInvited: 'Ansatt lagt til — de har fått e-post med lenke for å sette passord.',
      successAddedNoInvite: 'Ansatt lagt til. De trenger en passordlenke før de kan logge inn.',
      errorAddFailed: 'Kunne ikke legge til den ansatte.',
      thatPerson: 'Denne personen',
      errorDuplicate: '{name} er allerede registrert i {group}.',
      successSaved: 'Lagret.',
      errorSaveFailed: 'Kunne ikke lagre.',
      thisEmployee: 'denne ansatte',
      confirmDeactivateTitle: 'Deaktivere {name}?',
      confirmDeactivateMessage: 'De vil ikke kunne kjøpe noe på bedriftens konto lenger. Historikken beholdes, og du kan reaktivere når som helst.',
      confirmDeactivateLabel: 'Deaktiver',
      confirmRemoveTitle: 'Fjerne {name}?',
      confirmRemoveMessage: 'Dette fjerner personen fra bedriften. Bruk Deaktiver i stedet hvis de kan komme tilbake — fjerning er ment for noen lagt til ved en feil.',
      confirmRemoveLabel: 'Fjern',
      successReactivated: 'Ansatt reaktivert.',
      successDeactivated: 'Ansatt deaktivert.',
      errorStatusFailed: 'Kunne ikke endre status.',
      successRemoved: 'Ansatt fjernet.',
      errorRemoveFailed: 'Kunne ikke fjerne.',
      successResetSent: 'Passordlenke sendt til {email}',
      errorResetFailed: 'Kunne ikke sende lenken.',
      errorLoadFailed: 'Kunne ikke laste ansatte.'
    },

    employeeDialog: {
      addTitle: 'Legg til ansatt',
      editTitle: 'Rediger ansatt',
      fullName: 'Fullt navn',
      workEmail: 'Jobb-e-post',
      workEmailHint: 'De logger inn i kantinen med denne adressen.',
      email: 'E-post',
      employeeNumber: 'Ansattnummer (valgfritt)',
      group: 'Gruppe',
      subsidised: '{pct}% subsidiert',
      monthlyCredit: 'månedlig kreditt',
      groupHint: 'Bestemmer hva denne personen betaler.',
      sendInvite: 'Send dem en lenke for å sette passord nå',
      cancel: 'Avbryt',
      save: 'Lagre',
      errorNameRequired: 'Navn er påkrevd.',
      errorGroupRequired: 'Velg en gruppe.',
      errorEmailRequired: 'En gyldig jobb-e-post er påkrevd.'
    },

    ledgerDialog: {
      currentBalance: 'Nåværende saldo',
      colDate: 'Dato',
      colWhat: 'Hva',
      colAmount: 'Beløp',
      colBalance: 'Saldo',
      empty: 'Ingen saldohistorikk ennå.',
      close: 'Lukk',
      errorLoadFailed: 'Kunne ikke laste saldohistorikken.'
    },

    catering: {
      title: 'Møtemat',
      subtitle: 'Bestill mat fra ukens meny til et møte eller kurs. Bestillinger bekreftes av Frigg før noe tilberedes — dere får e-post uansett utfall.',
      noCanteen: 'Ingen kantine er koblet til bedriften ennå. Kontakt Frigg for å sette dette opp.',
      readOnly: 'Kontoen din kan se bestillinger, men ikke legge inn nye. Spør en eier eller leder hos dere.',
      newOrder: 'Ny bestilling',
      location: 'Sted',
      deliveryDate: 'Leveringsdato',
      datesNote: 'Kun datoer vi fortsatt kan planlegge for, vises.',
      loadingMenu: 'Laster menyen…',
      menuNotPublished: 'Menyen for den uken er ikke publisert ennå. Prøv en dato i en uke vi har planlagt.',
      menuNotFinal: 'Denne ukens meny er ikke endelig — et produkt kan fortsatt bli byttet. Vi sier ifra hvis det påvirker bestillingen deres.',
      colProduct: 'Produkt',
      colPrice: 'Pris (eks. mva)',
      colMin: 'Min',
      colQuantity: 'Antall',
      colSum: 'Sum',
      tagVegetarian: 'Vegetar',
      tagGlutenFree: 'Glutenfri',
      tagLactoseFree: 'Laktosefri',
      minWarning: 'min {min}',
      nothingAvailable: 'Ingenting på den dagens meny er tilgjengelig som møtemat. Prøv en annen dato.',
      whatFor: 'Referanse / PO',
      whatForPlaceholder: 'f.eks. Styremøte eller PO-nummer',
      comment: 'Kommentar (valgfritt)',
      commentPlaceholder: 'Allergier, leveringstid, rom…',
      belowMinOne: 'Ett produkt er under minste bestillingsantall — øk antallet eller fjern det.',
      belowMinMany: '{count} produkter er under minste bestillingsantall — øk antallet eller fjern dem.',
      belowOrderMin: 'Denne bestillingen er på {total} kr — bestillinger må være på minst {min} kr. Legg til mer for å sende den.',
      products: '{count} produkt',
      productsPlural: '{count} produkter',
      inclVat: 'inkl. mva',
      sendOrder: 'Send bestilling',
      sendingOrder: 'Sender…',
      yourOrders: 'Deres bestillinger',
      nothingOrdered: 'Ingenting bestilt ennå.',
      colOrderDate: 'Dato',
      colOrderNo: '#',
      colFor: 'Til',
      colProducts: 'Produkter',
      colTotal: 'Totalt',
      colOrderStatus: 'Status',
      tipCancel: 'Avbestill',
      statusConfirmed: 'Bekreftet',
      statusPending: 'Venter på godkjenning',
      statusRejected: 'Avslått',
      statusCancelled: 'Kansellert',
      statusUnknown: 'Ukjent',
      successSubmitted: 'Forespørsel #{number} sendt. Dere får e-post når Frigg bekrefter den.',
      errorSubmitFailed: 'Kunne ikke sende bestillingen.',
      confirmCancelTitle: 'Avbestille #{number}?',
      confirmCancelApproved: 'Denne bestillingen er allerede bekreftet av Frigg. Å avbestille nå varsler kjøkkenet.',
      confirmCancelPending: 'Denne forespørselen er ikke bekreftet ennå, så ingenting er tilberedt.',
      confirmCancelLabel: 'Avbestill',
      successCancelled: 'Bestilling avbestilt.',
      errorCancelFailed: 'Kunne ikke avbestille.',
      tooCloseToCancel: 'For nær leveringsdatoen til å avbestille — kontakt Frigg.',
      errorLoadFailed: 'Kunne ikke laste møtematbestilling.',
      errorMenuLoadFailed: 'Kunne ikke laste menyen for den datoen.'
    },

    purchases: {
      title: 'Kjøp',
      subtitle: 'Hva de ansatte har kjøpt, og hvor mye bedriften har subsidiert. Dette er en driftsoversikt — fakturaen er det gjeldende tallet.',
      month: 'Måned',
      group: 'Gruppe',
      allGroups: 'Alle grupper',
      searchPlaceholder: 'Søk ansatt eller produkt…',
      perEmployee: 'Per ansatt',
      allPurchases: 'Alle kjøp',
      export: 'Eksporter',
      loading: 'Laster kjøp…',
      statPurchases: 'Kjøp',
      statPaidByEmployees: 'Betalt av ansatte',
      statSubsidyFoot: 'etter deres subsidiering',
      statYourSubsidy: 'Deres subsidiering',
      statExVat: 'eks. mva',
      colEmployee: 'Ansatt',
      colEmail: 'E-post',
      colPurchases: 'Kjøp',
      colEmployeePaid: 'Betalt av ansatt',
      colYourSubsidy: 'Deres subsidiering',
      colDate: 'Dato',
      colProduct: 'Produkt',
      colPayment: 'Betaling',
      empty: 'Ingen kjøp i denne perioden.',
      errorLoadFailed: 'Kunne ikke laste kjøp.',
      csvSubsidyExVat: 'Bedriftens subsidiering (eks. mva)'
    },

    help: {
      title: 'Hjelp',
      subtitle: 'Hva hver side gjør, hva rollen din kan gjøre, og hvordan dere når Frigg for alt annet.',
      replayWelcome: 'Vis velkomstguiden igjen',
      pages: 'Sider',
      yourRole: 'Din rolle',
      colRole: 'Rolle',
      colManageEmployees: 'Administrere ansatte',
      colOrderCatering: 'Bestille møtemat',
      roleOwner: 'Eier',
      roleManager: 'Leder',
      roleViewer: 'Leser',
      yes: 'Ja',
      no: 'Nei',
      readOnly: 'Kun lesetilgang',
      signedInAsPrefix: 'Du er logget inn som',
      whatFriggHandles: 'Dette ordner Frigg',
      whatFriggHandlesIntro: 'Noen ting er kommersielle vilkår mellom bedriften og Frigg, ikke deres egne data, så de kan ikke endres her — kontakt din Frigg-kontakt for:',
      handledGroups: 'Opprette eller endre en gruppe (hvem som hører til hvor, subsidieprosent, månedlig kreditt)',
      handledCredit: 'Justere en ansatts saldo',
      handledInvoicing: 'Fakturering og betalingsspørsmål',
      handledPortalUsers: 'Legge til enda en portalbruker for bedriften'
    },

    welcome: {
      title: 'Velkommen',
      titleWithName: 'Velkommen, {name}',
      intro: 'Her administrerer dere kantineansatte selv, i stedet for å sende e-post til Frigg. Dette gjør hver side:',
      readOnlyNotice: 'Kontoen din har kun lesetilgang — du kan se alt her, men ikke gjøre endringer. Spør en eier eller leder hos dere hvis noe må oppdateres.',
      footnote: 'Trenger dere noe som ikke er her — nye grupper, endring av subsidiering, eller et faktura­spørsmål? Kontakt deres Frigg-kontakt.',
      readFullGuide: 'Les hele guiden',
      gotIt: 'Skjønner, takk'
    },

    guide: {
      overviewTitle: 'Oversikt',
      overviewBody: 'Et månedlig øyeblikksbilde av kontoen: hvor mange ansatte dere har, hva de har kjøpt, hva subsidieringen har kostet, og eventuell saldo som fortsatt står på kontoene deres. Bytt måned øverst for å se tilbake.',
      employeesTitle: 'Ansatte',
      employeesBody: 'Legg til en nyansatt eller deaktiver noen som har sluttet. Deaktivering stopper tilgangen umiddelbart, men beholder historikken, så dere kan reaktivere senere hvis de kommer tilbake. Klikk på et navn for å se saldohistorikken.',
      cateringTitle: 'Møtemat',
      cateringBody: 'Bestill fra ukens planlagte meny til et kommende møte eller kurs. Hver bestilling er en forespørsel — Frigg bekrefter den før noe tilberedes, og dere får e-post uansett utfall.',
      purchasesTitle: 'Kjøp',
      purchasesBody: 'En oversikt over alt som er kjøpt på bedriftens konto, så dere kan sjekke forbruket uten å be Frigg om en rapport.'
    },

    confirm: {
      cancel: 'Avbryt',
      confirm: 'Bekreft'
    }
  }
};
